"use client";

import { useEffect, useState } from "react";

interface VersionInfo {
  hash: string;
  shortHash: string;
  message: string;
  date: string;
  timestamp: string;
}

function formatCommitDate(dateString: string): string {
  try {
    const date = new Date(dateString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString;
    }

    return date.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

export function VersionDisplay() {
  const [showVersion, setShowVersion] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [versionInfo, setVersionInfo] = useState<VersionInfo>({
    hash: "unknown",
    shortHash: "unknown",
    message: "Loading...",
    date: new Date().toISOString(),
    timestamp: new Date().toISOString(),
  });

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setShowVersion(urlParams.has("version"));
  }, []);

  useEffect(() => {
    if (!showVersion) return;

    async function loadVersionInfo() {
      try {
        const versionModule = await import("@/version.json");
        setVersionInfo(versionModule.default);
      } catch (error) {
        console.log(error);
        setVersionInfo({
          hash: "unknown",
          shortHash: "unknown",
          message: "Development mode",
          date: new Date().toISOString(),
          timestamp: new Date().toISOString(),
        });
      }
    }

    void loadVersionInfo();
  }, [showVersion]);

  if (!showVersion || isClosed) {
    return null;
  }

  const formattedDate = formatCommitDate(versionInfo.date);

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="relative max-w-xs rounded-lg bg-gray-800 p-3 text-xs text-white opacity-80 shadow-lg">
        <button
          onClick={() => setIsClosed(true)}
          className="absolute -right-1 -top-1 flex size-5 items-center justify-center rounded-full bg-gray-600 text-gray-300 hover:bg-gray-500 hover:text-white"
          aria-label="Close version info"
        >
          ×
        </button>
        <div className="space-y-1">
          <div className="text-gray-200">{formattedDate}</div>
          <div className="leading-tight text-gray-100">
            {versionInfo.message}
          </div>
        </div>
      </div>
    </div>
  );
}
